// Legacy redirect: /o/{orgSlug}/projects/{programId-profileId} → /o/{orgSlug}/p/{profileSlug}/c/{campaignId}

import { projectsListQueryOptions, type Project } from "@/lib/queries/projects";
import { ensureOrgContext } from "@/lib/utils/org-context-guard";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { OrgRouteContext } from "../../route";

export const Route = createFileRoute("/(org)/o/$orgSlug/projects/$projectId")({
  beforeLoad: async ({ context, params }) => {
    const { organization } = context as OrgRouteContext;
    if (
      !ensureOrgContext(
        organization,
        "(org)/o.$orgSlug/projects/$projectId/route.tsx#beforeLoad",
        { orgSlug: params.orgSlug, projectId: params.projectId },
      )
    ) {
      throw redirect({ to: "/", search: { error: "organization_not_found" } });
    }

    // Parse composite projectId (format: "programId-profileId")
    const firstHyphen = params.projectId.indexOf("-");
    if (firstHyphen <= 0) {
      throw redirect({
        to: "/o/$orgSlug/projects",
        params: { orgSlug: params.orgSlug },
      });
    }

    const programIdStr = params.projectId.slice(0, firstHyphen);
    const profileId = params.projectId.slice(firstHyphen + 1);

    if (!/^\d+$/.test(programIdStr) || !profileId) {
      throw redirect({
        to: "/o/$orgSlug/projects",
        params: { orgSlug: params.orgSlug },
      });
    }

    const programId = parseInt(programIdStr, 10);

    // Look up the project to get profileSlug
    const projectsData = await context.queryClient.fetchQuery(
      projectsListQueryOptions(organization.id),
    );

    const project = (projectsData?.projects ?? []).find(
      (p: Project) => p.id === programId && p.profileId === profileId,
    );

    if (!project) {
      throw redirect({
        to: "/o/$orgSlug/projects",
        params: { orgSlug: params.orgSlug },
      });
    }

    // Redirect to the new URL structure
    throw redirect({
      to: "/o/$orgSlug/p/$profileSlug/c/$campaignId",
      params: {
        orgSlug: params.orgSlug,
        profileSlug: project.profileSlug,
        campaignId: String(programId),
      },
    });
  },
});
